import React, { useState } from 'react';
import { MessageTemplate, MessageTemplateProps } from '../types';

export const MessageTemplates: React.FC<MessageTemplateProps> = ({ templates, onUpdate }) => {
  const [newTemplate, setNewTemplate] = useState<Partial<MessageTemplate>>({
    title: '',
    content: '',
    variables: [],
    isActive: true
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewTemplate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVariableAdd = () => {
    const variableInput = document.getElementById('variable') as HTMLInputElement;
    const newVariable = variableInput.value.trim();
    if (newVariable) {
      setNewTemplate(prev => ({
        ...prev,
        variables: [...(prev.variables || []), newVariable]
      }));
      variableInput.value = '';
    }
  };

  const handleVariableRemove = (index: number) => {
    setNewTemplate(prev => ({
      ...prev,
      variables: prev.variables?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplate.title || !newTemplate.content) {
      return;
    }

    const template: MessageTemplate = {
      id: Date.now().toString(),
      title: newTemplate.title,
      content: newTemplate.content,
      variables: newTemplate.variables || [],
      isActive: newTemplate.isActive || true
    };

    onUpdate(template);
    setNewTemplate({
      title: '',
      content: '',
      variables: [],
      isActive: true
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Message Templates</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Create New Template</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Title
              </label>
              <input
                type="text"
                name="title"
                value={newTemplate.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter template title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Content
              </label>
              <textarea
                name="content"
                value={newTemplate.content}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter template content"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variables
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  id="variable"
                  className="flex-1 px-3 py-2 border rounded"
                  placeholder="Add a variable (e.g., {name})"
                />
                <button
                  type="button"
                  onClick={handleVariableAdd}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {newTemplate.variables?.map((variable, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100"
                  >
                    {variable}
                    <button
                      type="button"
                      onClick={() => handleVariableRemove(index)}
                      className="ml-2 text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={newTemplate.isActive}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                Active
              </label>
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create Template
            </button>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Existing Templates</h2>
          <div className="space-y-4">
            {templates.map(template => (
              <div
                key={template.id}
                className="border rounded p-4 hover:bg-gray-50"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm font-medium text-gray-900">{template.title}</h4>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      template.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {template.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{template.content}</p>
                {template.variables.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {template.variables.map((variable, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}; 